<!--
  Layout note: the page is left-aligned, like every other section on the site. It used to centre the
  hero, all three section heads and the CTA, which fought the site's heading pattern (accent bar +
  supporting line) and left a lot of dead space either side of short copy.
-->
<section class="sav-hvw-hero" aria-label="How we verify coupons">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs" />

    <div class="sav-hvw-hero-inner">
      <div class="sav-hvw-hero-copy">
        <span class="sav-hvw-eyebrow">Coupon Verification</span>
        <h1 class="sav-hvw-title">
          How Verification Works <app-dev-flag note="Static hero copy" />
        </h1>
        <p class="sav-hvw-subtitle">
          Every coupon on SavingArena goes through a rigorous process before it reaches you. Here's
          exactly how we keep offers accurate, safe and reliable.
        </p>

        <!-- The page explains a process; these are the two things a reader wants next. -->
        <div class="sav-hvw-hero-actions">
          <a class="sav-hvw-btn" routerLink="/coupons/verified">Browse verified coupons</a>
          <a class="sav-hvw-btn is-ghost" routerLink="/why-trust-us">Why trust us</a>
        </div>
      </div>

      <!-- A summary of the process itself, so the hero carries substance rather than white space.
           Each line is one of the six stages below, not a claim or a statistic. -->
      <aside class="sav-hvw-hero-card" aria-label="The check in short">
        <p class="sav-hvw-hero-card-title">Every offer passes</p>
        <ul class="sav-hvw-hero-list">
          @for (step of steps; track step.title) {
            <li>
              <span class="sav-hvw-hero-tick" aria-hidden="true">✓</span>
              {{ step.title }}
            </li>
          }
        </ul>
      </aside>
    </div>
  </div>
</section>

<section class="sav-hvw-steps-section" aria-label="Our verification process">
  <div class="sav-site-container">
    <span class="sav-hvw-eyebrow">Our Process</span>
    <app-section-heading
      heading="A six-step check on every coupon"
      subtitle="Before any offer is published, it passes through each of these stages."
    >
      <app-dev-flag note="Static verification steps (hardcoded array)" />
    </app-section-heading>

    <!-- An ordered list, because the stages happen in order — the previous grid of six equal cards
         said nothing about sequence. The rail and numbered nodes carry that. -->
    <ol class="sav-hvw-timeline">
      @for (step of steps; track step.title; let i = $index) {
        <li class="sav-hvw-step">
          <span class="sav-hvw-step-node" aria-hidden="true">{{
            (i + 1).toString().padStart(2, '0')
          }}</span>
          <div class="sav-hvw-step-body">
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </li>
      }
    </ol>
  </div>
</section>

<section class="sav-hvw-faq-section" aria-label="Frequently asked questions">
  <div class="sav-site-container">
    <span class="sav-hvw-eyebrow">Good to know</span>
    <app-section-heading
      heading="Frequently asked questions"
      subtitle="Common questions about how verification works."
    >
      <app-dev-flag note="Static FAQ block (hardcoded array)" />
    </app-section-heading>

    <!-- Shared FAQ accordion — see `.sav-faq-*` in styles.scss. -->
    <div class="sav-faq-list">
      @for (faq of faqs; track faq.question; let i = $index) {
        <div class="sav-faq-item" [class.expanded]="expandedFaq() === i">
          <button
            type="button"
            class="sav-faq-question"
            [attr.aria-expanded]="expandedFaq() === i"
            (click)="toggleFaq(i)"
          >
            {{ faq.question }}
          </button>
          <div class="sav-faq-answer" [class.visible]="expandedFaq() === i">
            <div class="sav-faq-answer-body">{{ faq.answer }}</div>
          </div>
        </div>
      }
    </div>
  </div>
</section>

<section class="sav-hvw-cta-section" aria-label="Learn more">
  <div class="sav-site-container">
    <div class="sav-hvw-cta-card">
      <div class="sav-hvw-cta-copy">
        <h2>Want to know why shoppers trust us?</h2>
        <p>See the verified coupons, secure deals and standards that make SavingArena reliable.</p>
      </div>
      <div class="sav-hvw-cta-actions">
        <a class="sav-hvw-btn" routerLink="/why-trust-us">Why Trust SavingArena?</a>
        <a class="sav-hvw-btn is-ghost" routerLink="/submit-coupon">Report a broken coupon</a>
      </div>
    </div>
  </div>
</section>

<app-big-sale-banner />
